export type MetadataAction = 'mask' | 'obfuscate' | 'preserve' | 'remove';
export type MaskPolicy = 'replace_with_x' | 'blackout' | 'blank_value' | 'hash_value';

export interface ImagingStudy {
    study_id: string;
    patient_id: string;
    patient_name?: string;
    study_date?: string;
    study_description?: string;
    modality?: string;
    series_count?: number;
    instance_count?: number;
    file_path?: string;
    file_size?: number;
    status?: 'pending' | 'processing' | 'completed' | 'error';
    phi_detected?: boolean;
    phi_anonymized?: boolean;
    dataSource?: string;
    has_sr_files?: boolean;
}

export interface ImagingSlice {
    slice_id: string;
    series_id: string;
    instance_number?: number;
    slice_location?: number;
    file_path?: string;
    content_type?: 'image' | 'sr' | 'pdf' | 'text';
    sop_class_uid?: string;
    modality?: 'CT' | 'MR' | 'US' | 'DX' | 'MG' | 'NM' | 'PT' | 'CR' | 'XA' | 'RF' | 'OT' | 'SR';
    phi_detected?: boolean;
    phi_anonymized?: boolean;
    bounding_boxes?: PHIBoundingBox[];
    sr_content?: string;
    sr_original_content?: string;
}

export interface PHIBoundingBox {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    entity_type:
        | 'NAME'
        | 'MRN'
        | 'DOB'
        | 'ACCESSION'
        | 'PHYSICIAN'
        | 'INSTITUTION'
        | 'DEVICE_ID'
        | 'STUDY_ID'
        | 'FREE_TEXT';
    detected_text?: string;
    confidence?: number;
    status?: 'pending' | 'accepted' | 'rejected';
    slice_id?: string;
}

export interface PHIDecision {
    entity_id: string;
    entity_type: PHIBoundingBox['entity_type'];
    detected_text: string;
    decision: 'accepted' | 'rejected';
    applied_to_all: boolean;
    affected_slices: string[];
    timestamp?: string;
}

export interface MetadataTagDecision {
    tag: string;
    action: MetadataAction;
    applyToAll: boolean;
    originalValue?: any;
    anonymizedValue?: any;
    maskPolicy?: MaskPolicy;
}

export interface PHITag {
    id: string;
    name: string;
}

export interface CustomPixelEntity {
    key: string;
    label: string;
    tagId: string;
}

export interface PHITagSet {
    id: string;
    name: string;
    description?: string;
    tags: PHITag[];
}

export interface DeIDProfile {
    id: string;
    name: string;
    description?: string;
    phiTagSetId: string;
    profileType?: 'imaging' | 'unstructured' | 'structured' | 'mixed';
    pixelAction?: 'pixelate' | 'blur' | 'mask';
    pixelMaskPolicies?: Record<string, MaskPolicy>;
    customPixelEntities?: CustomPixelEntity[];
    excludedPixelEntities?: string[];
    metadataActions: Record<string, MetadataAction>;
    metadataMaskPolicies?: Record<string, MaskPolicy>;
    customMetadataTags?: PHITag[];
    excludedMetadataTags?: string[];
    // PJ-2057: Only "mask" and "obfuscate" are supported by the backend API
    textAction?: 'mask' | 'obfuscate';
    textMaskPolicy?: MaskPolicy;
    isPreset?: boolean;
    isCustom?: boolean;
    created_at?: string;
    updated_at?: string;
}

export type DeIDStatus = 'pending' | 'in_progress' | 'ready_to_review' | 'completed' | 'failed';

export interface MetadataDecision {
    tag: string;
    action: MetadataAction;
    appliedToAll: boolean;
    affectedSlices: string[];
    timestamp?: string;
}

export interface DeIDPipelineResult {
    studyId: string;
    originalSlices: ImagingSlice[];
    anonymizedSlices: ImagingSlice[];
    pixelDecisions: PHIDecision[];
    metadataDecisions: MetadataDecision[];
    executionTime?: number;
    profileUsed: DeIDProfile;
    status: 'pending' | 'processing' | 'completed' | 'error';
    error?: string;
}

export interface DeIDResult {
    id: string;
    name: string;
    status: DeIDStatus;
    profileId: string;
    profileName: string;
    datasetId?: string;
    datasetName?: string;
    cohortId?: string;
    cohortName?: string;
    sourceId?: string;
    sourceName?: string;
    datastoreId?: string;
    deidDatastoreId?: string;
    version: number;
    patientCount: number;
    studyCount: number;
    sliceCount: number;
    phiDetectedCount: number;
    successfulAnonymizations: number;
    failedAnonymizations: number;
    pendingReview: number;
    progress?: number;
    error?: string;
    created_at: string;
    completed_at?: string;
    executionTime?: number;
    createdBy: string;
    modalities?: string[];
    avgTextDetectionConfidence?: number;
    avgPHIExtractionConfidence?: number;
    documentCount?: number;
    processedDocuments?: number;
    failedDocuments?: number;
    processingDocuments?: number;
    apiResponseMetadata?: Record<string, any>;
    sessionId?: string; // Session ID from DeID API for status polling and entity corrections
    /** Inferred from profile type (imaging | unstructured | structured | mixed) for DeID Type column */
    deidType?: 'structured' | 'unstructured' | 'imaging' | 'mixed';
    /** Dataset-level counts (from linked dataset) for Count column */
    datasetPatientCount?: number;
    datasetDocumentCount?: number;
    datasetImageCount?: number;
}

